import { createContext, useContext, useEffect } from "react";

/** A single crumb. Crumbs with `to` render as links; the last crumb is the current page. */
export interface BreadcrumbEntry {
  label: string;
  to?: string;
}

export interface BreadcrumbsContextValue {
  items: BreadcrumbEntry[];
  setItems: (items: BreadcrumbEntry[]) => void;
}

export const BreadcrumbsContext = createContext<BreadcrumbsContextValue | null>(
  null,
);

/**
 * Publishes the page's breadcrumb trail to the layout header while the
 * calling component is mounted; the header falls back to "Admin Console"
 * after unmount. Safe to call with labels that resolve asynchronously —
 * the trail updates when `items` changes.
 */
export function useBreadcrumbs(items: BreadcrumbEntry[]) {
  const ctx = useContext(BreadcrumbsContext);
  if (!ctx) {
    throw new Error("useBreadcrumbs must be used within BreadcrumbsProvider");
  }
  const { setItems } = ctx;
  const itemsKey = JSON.stringify(items);
  useEffect(() => {
    setItems(JSON.parse(itemsKey) as BreadcrumbEntry[]);
    return () => setItems([]);
  }, [itemsKey, setItems]);
}
